import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';

interface PWAInstallButtonProps {
  className?: string;
  variant?: 'sidebar' | 'primary' | 'outline';
}

export const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ className = '', variant = 'sidebar' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // If it's already installed or running in standalone mode, don't show the button
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    
    // We've used the prompt, and can't use it again, throw it away
    setDeferredPrompt(null);
  };

  if (!isInstallable) return null;

  if (variant === 'sidebar') {
    return (
      <button
        onClick={handleInstallClick}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-primary dark:text-golden hover:bg-primary/10 dark:hover:bg-golden/10 w-full ${className}`}
      >
        <Download size={20} className="flex-shrink-0" />
        <span className="font-medium whitespace-nowrap overflow-hidden transition-all duration-300">
          Download App
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={handleInstallClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        variant === 'primary' 
          ? 'bg-primary text-white hover:bg-primary/90' 
          : 'border border-primary text-primary hover:bg-primary/10 dark:border-golden dark:text-golden dark:hover:bg-golden/10'
      } ${className}`}
    >
      <Download size={18} />
      <span>Install App</span>
    </button>
  );
};
